import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Save, 
  Loader2, 
  AlertCircle, 
  Image as ImageIcon,
  Zap,
  Heart,
  Shield,
  Swords,
  Star,
  Sparkles,
  Upload,
  RefreshCw
} from 'lucide-react';
import { getPokemonImageUrl, getPokemonImageSrcList } from '../../../utils/imageUtils';

const PokemonEditModal = ({ isOpen, onClose, pokemon, onSave }) => {
  const [formData, setFormData] = useState({
    Pokemon_ID: '',
    Pokemon_Name_EN: '',
    Pokemon_Name_TH: '',
    types: '',
    primaryType: '',
    secondaryType: '',
    HP: '',
    Attack: '',
    Defense: '',
    Max_CP_Lv50: '',
    Gigantamax_Move: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('รองรับเฉพาะไฟล์ jpg, jpeg, png, gif, webp');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('ไฟล์ต้องมีขนาดไม่เกิน 5MB');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      formDataUpload.append('pokemon_id', formData.Pokemon_ID);

      const response = await fetch('/api/upload/pokemon-image', {
        method: 'POST',
        body: formDataUpload
      });

      const result = await response.json();
      
      if (result.success) {
        setImagePreview(result.data.path + '?t=' + Date.now()); // Add timestamp to bust cache
      } else {
        setUploadError(result.message || 'อัปโหลดไม่สำเร็จ');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('เกิดข้อผิดพลาดในการอัปโหลด');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (pokemon) {
      const types = pokemon.types || '';
      const typeArray = types.split(',');
      const primaryType = typeArray[0] || '';
      const secondaryType = typeArray[1] || '';
      
      setFormData({
        Pokemon_ID: pokemon.Pokemon_ID || '',
        Pokemon_Name_EN: pokemon.Pokemon_Name_EN || '',
        Pokemon_Name_TH: pokemon.Pokemon_Name_TH || '',
        types: types,
        primaryType: primaryType,
        secondaryType: secondaryType,
        HP: pokemon.HP || '',
        Attack: pokemon.Attack || '',
        Defense: pokemon.Defense || '',
        Max_CP_Lv50: pokemon.Max_CP_Lv50 || '',
        Gigantamax_Move: pokemon.Gigantamax_Move || ''
      });
      setImagePreview(getPokemonImageUrl(pokemon.Pokemon_ID));
    }
  }, [pokemon]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.Pokemon_ID.trim()) newErrors.Pokemon_ID = 'กรุณากรอก ID';
    if (!formData.Pokemon_Name_EN.trim()) newErrors.Pokemon_Name_EN = 'กรุณากรอกชื่ออังกฤษ';
    if (!formData.Pokemon_Name_TH.trim()) newErrors.Pokemon_Name_TH = 'กรุณากรอกชื่อไทย';
    if (!formData.primaryType.trim()) newErrors.types = 'กรุณาเลือกประเภทหลัก';
    if (!formData.HP || isNaN(formData.HP)) newErrors.HP = 'กรุณากรอก HP ที่ถูกต้อง';
    if (!formData.Attack || isNaN(formData.Attack)) newErrors.Attack = 'กรุณากรอก Attack ที่ถูกต้อง';
    if (!formData.Defense || isNaN(formData.Defense)) newErrors.Defense = 'กรุณากรอก Defense ที่ถูกต้อง';
    if (!formData.Max_CP_Lv50 || isNaN(formData.Max_CP_Lv50)) newErrors.Max_CP_Lv50 = 'กรุณากรอก Max CP ที่ถูกต้อง';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving Pokemon:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getTypeColor = (type) => {
    const typeColors = {
      'Fire': 'bg-red-500',
      'Water': 'bg-blue-500',
      'Grass': 'bg-green-500',
      'Electric': 'bg-yellow-500',
      'Psychic': 'bg-purple-500',
      'Ice': 'bg-cyan-500',
      'Dragon': 'bg-indigo-500',
      'Dark': 'bg-gray-800',
      'Fairy': 'bg-pink-500',
      'Fighting': 'bg-orange-500',
      'Poison': 'bg-violet-500',
      'Ground': 'bg-amber-600',
      'Flying': 'bg-sky-500',
      'Bug': 'bg-lime-500',
      'Rock': 'bg-stone-500',
      'Ghost': 'bg-purple-600',
      'Steel': 'bg-gray-500',
      'Normal': 'bg-gray-400'
    };
    return typeColors[type] || 'bg-gray-400';
  };

  const formatTypes = (typesString) => {
    if (!typesString) return [];
    return typesString.split(',').map(type => type.trim());
  };

  const types = formatTypes(formData.types);
  const totalStats = (parseInt(formData.HP) || 0) + (parseInt(formData.Attack) || 0) + (parseInt(formData.Defense) || 0);
  const isLegendary = (parseInt(formData.Max_CP_Lv50) || 0) > 4000;
  const isMythical = (parseInt(formData.Max_CP_Lv50) || 0) > 5000;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col mx-2 sm:mx-0"
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: '95vh' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">แก้ไขโปเกมอน</h2>
                <p className="text-blue-100 text-sm">แก้ไขข้อมูลโปเกมอนในระบบ</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1 min-h-0">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Pokemon Preview */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-xl p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
                  {/* Image Preview with Upload */}
                  <div className="flex flex-col items-center space-y-3">
                    <div className="relative w-32 h-32 bg-white dark:bg-gray-600 rounded-xl flex items-center justify-center shadow-lg">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt={formData.Pokemon_Name_EN}
                          className="w-full h-full object-contain p-2"
                          onError={(e) => {
                            e.target.src = '/images/pokemon/0000.webp';
                          }}
                        />
                      ) : (
                        <ImageIcon className="w-12 h-12 text-gray-400" />
                      )}
                      {uploading && (
                        <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                          <RefreshCw className="w-8 h-8 text-white animate-spin" />
                        </div>
                      )}
                      {(isLegendary || isMythical) && (
                        <div className="absolute -top-2 -right-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isMythical ? 'bg-purple-500' : 'bg-yellow-500'
                          }`}>
                            {isMythical ? (
                              <Sparkles className="w-4 h-4 text-white" />
                            ) : (
                              <Star className="w-4 h-4 text-white" />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Upload Button */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || !formData.Pokemon_ID}
                      className="flex items-center px-3 py-2 text-sm bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                    >
                      {uploading ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          กำลังอัปโหลด...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          เปลี่ยนรูป
                        </>
                      )}
                    </button>
                    {uploadError && (
                      <p className="text-xs text-red-500">{uploadError}</p>
                    )}
                    {!formData.Pokemon_ID && (
                      <p className="text-xs text-gray-500">กรุณากรอก ID ก่อนอัปโหลดรูป</p>
                    )}
                  </div>

                  {/* Pokemon Info Preview */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formData.Pokemon_Name_EN || 'ชื่ออังกฤษ'}
                      </h3>
                      {(isLegendary || isMythical) && (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          isMythical ? 'bg-purple-500' : 'bg-yellow-500'
                        }`}>
                          {isMythical ? (
                            <Sparkles className="w-3 h-3 text-white" />
                          ) : (
                            <Star className="w-3 h-3 text-white" />
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-lg text-gray-600 dark:text-gray-400 mb-3">
                      {formData.Pokemon_Name_TH || 'ชื่อไทย'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 font-mono mb-4">
                      #{formData.Pokemon_ID || '0000'}
                    </p>
                    
                    {/* Types Preview */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {types.map((type, idx) => (
                        <span
                          key={idx}
                          className={`px-3 py-1 text-xs font-medium text-white rounded-full ${getTypeColor(type)}`}
                        >
                          {type}
                        </span>
                      ))}
                    </div>

                    {/* Stats Preview */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-1 mb-1">
                          <Heart className="w-4 h-4 text-red-500" />
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">HP</span>
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {formData.HP || '0'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-1 mb-1">
                          <Swords className="w-4 h-4 text-orange-500" />
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">ATK</span>
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {formData.Attack || '0'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-1 mb-1">
                          <Shield className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">DEF</span>
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {formData.Defense || '0'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-1 mb-1">
                          <Zap className="w-4 h-4 text-yellow-500" />
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">CP</span>
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {formData.Max_CP_Lv50 || '0'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Total Stats */}
                    <div className="mt-4 text-center">
                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Stats</div>
                      <div className="text-2xl font-bold text-green-600">{totalStats.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                    ข้อมูลพื้นฐาน
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Pokemon ID *
                    </label>
                    <input
                      type="text"
                      value={formData.Pokemon_ID}
                      onChange={(e) => handleInputChange('Pokemon_ID', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.Pokemon_ID ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="0001"
                    />
                    {errors.Pokemon_ID && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.Pokemon_ID}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      ชื่ออังกฤษ *
                    </label>
                    <input
                      type="text"
                      value={formData.Pokemon_Name_EN}
                      onChange={(e) => handleInputChange('Pokemon_Name_EN', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.Pokemon_Name_EN ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="Bulbasaur"
                    />
                    {errors.Pokemon_Name_EN && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.Pokemon_Name_EN}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      ชื่อไทย *
                    </label>
                    <input
                      type="text"
                      value={formData.Pokemon_Name_TH}
                      onChange={(e) => handleInputChange('Pokemon_Name_TH', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.Pokemon_Name_TH ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="ฟุชิกิดาเนะ"
                    />
                    {errors.Pokemon_Name_TH && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.Pokemon_Name_TH}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      ประเภท *
                    </label>
                    <div className="space-y-3">
                      {/* Primary Type */}
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">ประเภทหลัก</label>
                        <select
                          value={formData.primaryType || ''}
                          onChange={(e) => {
                            const primaryType = e.target.value;
                            const secondaryType = formData.secondaryType || '';
                            const types = secondaryType ? `${primaryType},${secondaryType}` : primaryType;
                            handleInputChange('types', types);
                            setFormData(prev => ({ ...prev, primaryType, types }));
                            
                            // Reset secondary type if it's the same as primary type
                            if (secondaryType === primaryType) {
                              setFormData(prev => ({ ...prev, secondaryType: '', types: primaryType }));
                            }
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.types ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                        >
                          <option value="">เลือกประเภทหลัก</option>
                          <option value="Normal">Normal</option>
                          <option value="Fire">Fire</option>
                          <option value="Water">Water</option>
                          <option value="Electric">Electric</option>
                          <option value="Grass">Grass</option>
                          <option value="Ice">Ice</option>
                          <option value="Fighting">Fighting</option>
                          <option value="Poison">Poison</option>
                          <option value="Ground">Ground</option>
                          <option value="Flying">Flying</option>
                          <option value="Psychic">Psychic</option>
                          <option value="Bug">Bug</option>
                          <option value="Rock">Rock</option>
                          <option value="Ghost">Ghost</option>
                          <option value="Dragon">Dragon</option>
                          <option value="Dark">Dark</option>
                          <option value="Steel">Steel</option>
                          <option value="Fairy">Fairy</option>
                        </select>
                      </div>
                      
                      {/* Secondary Type */}
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">ประเภทรอง (ไม่บังคับ)</label>
                        <select
                          value={formData.secondaryType || ''}
                          onChange={(e) => {
                            const secondaryType = e.target.value;
                            const primaryType = formData.primaryType || '';
                            const types = secondaryType ? `${primaryType},${secondaryType}` : primaryType;
                            handleInputChange('types', types);
                            setFormData(prev => ({ ...prev, secondaryType, types }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          disabled={!formData.primaryType}
                        >
                          <option value="">{formData.primaryType ? 'ไม่มีประเภทรอง' : 'เลือกประเภทหลักก่อน'}</option>
                          {formData.primaryType && (
                            <>
                              {formData.primaryType !== 'Normal' && <option value="Normal">Normal</option>}
                              {formData.primaryType !== 'Fire' && <option value="Fire">Fire</option>}
                              {formData.primaryType !== 'Water' && <option value="Water">Water</option>}
                              {formData.primaryType !== 'Electric' && <option value="Electric">Electric</option>}
                              {formData.primaryType !== 'Grass' && <option value="Grass">Grass</option>}
                              {formData.primaryType !== 'Ice' && <option value="Ice">Ice</option>}
                              {formData.primaryType !== 'Fighting' && <option value="Fighting">Fighting</option>}
                              {formData.primaryType !== 'Poison' && <option value="Poison">Poison</option>}
                              {formData.primaryType !== 'Ground' && <option value="Ground">Ground</option>}
                              {formData.primaryType !== 'Flying' && <option value="Flying">Flying</option>}
                              {formData.primaryType !== 'Psychic' && <option value="Psychic">Psychic</option>}
                              {formData.primaryType !== 'Bug' && <option value="Bug">Bug</option>}
                              {formData.primaryType !== 'Rock' && <option value="Rock">Rock</option>}
                              {formData.primaryType !== 'Ghost' && <option value="Ghost">Ghost</option>}
                              {formData.primaryType !== 'Dragon' && <option value="Dragon">Dragon</option>}
                              {formData.primaryType !== 'Dark' && <option value="Dark">Dark</option>}
                              {formData.primaryType !== 'Steel' && <option value="Steel">Steel</option>}
                              {formData.primaryType !== 'Fairy' && <option value="Fairy">Fairy</option>}
                            </>
                          )}
                        </select>
                      </div>
                    </div>
                    {errors.types && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.types}
                      </p>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                    สถิติ
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      HP *
                    </label>
                    <input
                      type="number"
                      value={formData.HP}
                      onChange={(e) => handleInputChange('HP', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.HP ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="128"
                    />
                    {errors.HP && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.HP}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Attack *
                    </label>
                    <input
                      type="number"
                      value={formData.Attack}
                      onChange={(e) => handleInputChange('Attack', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.Attack ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="118"
                    />
                    {errors.Attack && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.Attack}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Defense *
                    </label>
                    <input
                      type="number"
                      value={formData.Defense}
                      onChange={(e) => handleInputChange('Defense', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.Defense ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="111"
                    />
                    {errors.Defense && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.Defense}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Max CP (Level 50) *
                    </label>
                    <input
                      type="number"
                      value={formData.Max_CP_Lv50}
                      onChange={(e) => handleInputChange('Max_CP_Lv50', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.Max_CP_Lv50 ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="1260"
                    />
                    {errors.Max_CP_Lv50 && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.Max_CP_Lv50}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Gigantamax Move
                    </label>
                    <input
                      type="text"
                      value={formData.Gigantamax_Move}
                      onChange={(e) => handleInputChange('Gigantamax_Move', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="G-Max Vine Lash"
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row items-center justify-end space-y-2 sm:space-y-0 sm:space-x-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>กำลังบันทึก...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>บันทึกการเปลี่ยนแปลง</span>
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PokemonEditModal;
